/**
 * Opens the in-app feedback sheet from outside FeedbackFab (de-slop ADM-12).
 *
 * FeedbackFab is mounted once in app/_layout.tsx and owns the sheet. On
 * phone-width web the floating button would sit on the WebAppShell bottom bar
 * and the chat composer, so the shell's 56px phone header shows a feedback
 * icon instead and calls `openFeedback()`. Pure module (no React Native) so it
 * can be unit-tested.
 */
import { BREAKPOINTS } from '../../hooks/breakpoints';

type Listener = () => void;

const openListeners = new Set<Listener>();
const availabilityListeners = new Set<Listener>();

function notifyAvailability() {
  availabilityListeners.forEach((listener) => listener());
}

/** FeedbackFab registers here while it can show the sheet (authenticated). */
export function registerFeedbackOpener(listener: Listener): () => void {
  openListeners.add(listener);
  notifyAvailability();
  return () => {
    openListeners.delete(listener);
    notifyAvailability();
  };
}

/** Opens the feedback sheet. Returns false when no sheet is mounted. */
export function openFeedback(): boolean {
  if (openListeners.size === 0) return false;
  openListeners.forEach((listener) => listener());
  return true;
}

/** Whether a feedback sheet is mounted (for useSyncExternalStore). */
export function isFeedbackAvailable(): boolean {
  return openListeners.size > 0;
}

export function subscribeFeedbackAvailability(listener: Listener): () => void {
  availabilityListeners.add(listener);
  return () => {
    availabilityListeners.delete(listener);
  };
}

/**
 * The floating button is used everywhere except phone-width web, where the
 * WebAppShell phone header carries the feedback entry instead.
 */
export function usesHeaderFeedbackEntry(platform: string, width: number): boolean {
  return platform === 'web' && width < BREAKPOINTS.tablet;
}
